import { ThrowError } from "@/api/callApi"
import CommonModal from "@/components/common/CommonModal"
import CommonTable, { type TableColumn } from "@/components/common/CommonTable"
import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { Button } from "@/components/ui/button"
import type { campus } from "@/interface/campus.interface"
import { CampusService } from "@/services/Campus/CampusService"
import { showToastNotification } from "@/utility/helper"
import { RoutesLink } from "@/utility/RouteLinks"
import { Pencil, Trash, TriangleAlert } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

const CampusConfig = () => {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [campusData, setCampusData] = useState<campus[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    const getCampus = async () => {
        try {
            const response = await CampusService.getCampus();
            if (response?.status) {
                setCampusData(response?.data?.campus);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getCampus();
    }, []);

    const toggleDeleteDialog = (id: string | undefined) => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen(!deleteDialogOpen);
    }

    const handleDeleteCampus = async () => {
        try {
            const response = await CampusService.deleteCampus(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message)
                toggleDeleteDialog("")
                getCampus();
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    const columns: TableColumn<campus>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: campus, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'campusName',
            header: 'Campus Name',
            render: (item: campus) => (
                <span className="font-semibold capitalize">{item?.name}</span>
            )
        },
        {
            key: 'siteName',
            header: 'Site Name',
            render: (item: campus) => (
                <span className="font-semibold capitalize">{item?.site?.name}</span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: campus) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.status ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
                    {item?.status ? 'True' : 'False'}
                </span>
            )
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: campus) => (
                <div className="flex gap-2">
                    <Button
                        size={'icon'}
                        onClick={() => navigate(`${RoutesLink.editCampus}/${item?.id}`)}
                        className="bg-primary/10 text-primary hover:bg-primary/20"
                        title="Edit Campus"
                    >
                        <Pencil size={18} />
                    </Button>
                    <Button
                        size={'icon'}
                        onClick={() => toggleDeleteDialog(item?.id)}
                        className="bg-destructive/10 text-destructive hover:bg-destructive/20"
                        title="Delete Campus"
                    >
                        <Trash size={18} />
                    </Button>
                </div>
            ),
        },
    ]



    return (
        <Layout >
            <div className="space-y-6">
                <MainHeading
                    title="Campus"
                    buttonText="Add Campus"
                    onClick={() => navigate(RoutesLink.addCampus)}
                    isBackButtonVisible={true}
                />
                <CommonTable data={campusData} columns={columns} loading={isLoading} />
            </div>
            <CommonModal
                title=""
                show={deleteDialogOpen}
                onClose={() => toggleDeleteDialog("")}
                className="max-w-md rounded-2xl p-0 overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <TriangleAlert className="text-red-600 w-8 h-8" />
                        </div>

                        <h2 className="mt-5 text-2xl font-bold text-gray-900">
                            Delete Campus?
                        </h2>

                        <p className="mt-3 text-sm leading-6 text-gray-500">
                            This action cannot be undone. Once deleted, all campus data will be
                            permanently removed from our servers.
                        </p>
                    </div>

                    <div className="mt-8 flex gap-3">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => toggleDeleteDialog("")}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={handleDeleteCampus}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </CommonModal>
        </Layout>
    )
}

export default CampusConfig